import { AxisSourceType, AxisSourceValue } from './types';

export const sanitizeSelectedComponentTypes = (
  selectedComponentTypes: string[],
  availableTypeNames: ReadonlySet<string>
): string[] =>
  selectedComponentTypes.filter(type => availableTypeNames.has(type));

/**
 * The sanitized `selectedComponentTypes` to persist when the stored selection
 * references types absent from the current dataset, or `null` when the stored
 * value is already valid and should be left untouched. Returns `null` while the
 * dataset has no types yet (loading / empty scope) so a stale selection isn't
 * cleared prematurely.
 */
export const getSanitizedComponentTypesToPersist = (
  selectedComponentTypes: string[],
  availableComponentTypes: readonly string[]
): string[] | null => {
  if (
    selectedComponentTypes.length === 0 ||
    availableComponentTypes.length === 0
  )
    return null;
  const sanitized = sanitizeSelectedComponentTypes(
    selectedComponentTypes,
    new Set(availableComponentTypes)
  );
  return sanitized.length === selectedComponentTypes.length ? null : sanitized;
};

export const isFieldSourceValue = (
  value: AxisSourceValue | null
): value is Extract<AxisSourceValue, { type: AxisSourceType.FIELD }> =>
  value?.type === AxisSourceType.FIELD;

export const isRefSourceValue = (
  value: AxisSourceValue | null
): value is Extract<AxisSourceValue, { type: AxisSourceType.REF }> =>
  value?.type === AxisSourceType.REF;

export const isParentSourceValue = (
  value: AxisSourceValue | null
): value is Extract<AxisSourceValue, { type: AxisSourceType.PARENT }> =>
  value?.type === AxisSourceType.PARENT;

export const getAxisSourceKey = (value: AxisSourceValue): string => {
  if (isFieldSourceValue(value))
    return `${AxisSourceType.FIELD}:${value.fieldName}`;
  if (isParentSourceValue(value)) return AxisSourceType.PARENT;
  return [
    AxisSourceType.REF,
    value.ref.referenceWorkspaceId,
    value.ref.referenceTypeId,
    value.ref.linkedWorkspaceId,
    value.ref.isOutgoing,
    value.ref.linkedComponentTypeName ?? '',
  ].join('|');
};
